import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Start a new page at the top.
 *
 * A single-page app does not reload, so the window keeps whatever scroll
 * position the last page left behind: tap a link from halfway down a long list
 * and the next page opens halfway down too, often below its own heading.
 *
 * Going back is the exception. Someone pressing back is returning to something
 * they were already looking at, and throwing them to the top of it loses their
 * place — so a POP navigation is left alone and the browser's own restoration
 * does its job.
 *
 * Only the pathname is watched. A changing query string is usually a filter or
 * a page number on the screen the person is already reading, and scrolling them
 * away from the control they just used would be its own bug.
 */
export function ScrollToTop() {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (navigationType === "POP") return;
    // Instant rather than smooth: a new page animating its way up reads as the
    // page still loading, and it fights a reduced-motion preference.
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, navigationType]);

  return null;
}

/**
 * Bring an element to the top of the viewport. For flows that advance without
 * changing the route — the booking steps — where ScrollToTop never fires.
 */
export function scrollToTop() {
  window.scrollTo({ top: 0, left: 0, behavior: "instant" });
}
